<template>
    <Head :title="title" :aria-label="title"/>

    <div>
        <div
            class="bg-player-background overflow-hidden rounded-[16px] sticky top-0 left-0 right-0 z-10 p-6 max-[320px]:p-4"
        >
            <div class="flex flex-wrap sm:flex-nowrap items-center">
                <div class="w-full justify-center sm:w-auto sm:pr-8 mb-8 sm:mb-0 shrink-0 hidden sm:flex">
                    <img
                        v-if="currentEpisode"
                        :src="art"
                        class="h-[160px] sm:h-[192px] w-[160px] sm:w-[192px] aspect-square rounded-[6px]"
                        :alt="currentEpisode.title"
                    />
                </div>
                <div class="w-full min-w-0">
                    <div class="w-full relative max-w-full">
                        <div class="font-bold mb-2 w-full min-w-0">
                            <p
                                v-if="playlist[currentTrack]"
                                class="text-player-contrast text-[12px] font-medium mb-3 pr-[100px] truncate"
                                :title="podcast.title"
                                :aria-label="podcast.title"
                            >
                                {{ podcast.title }}
                            </p>
                            <p
                                v-if="playlist[currentTrack]"
                                class="text-[18px] font-bold text-player-contrast truncate"
                                :title="playlist[currentTrack].title"
                                :aria-label="playlist[currentTrack].title"
                            >
                                S{{ playlist[currentTrack].season }} / E{{ playlist[currentTrack].episode }}
                                {{ playlist[currentTrack].title }}
                            </p>
                        </div>

                        <div class="shrink-0 absolute right-0 top-0" v-if="podcast?.branding">
                            <a href="https://www.springcast.io" class="width: 84px; height: 18px">
                                <img src="/img/springcast_logo.svg" alt="Springcast" width="84" height="18" v-if="logoColor === 'light'" />
                                <img src="/img/springcast_logo_dark.svg" alt="Springcast" width="84" height="18" v-else />
                            </a>
                        </div>
                    </div>

                    <Timeline @ended="finalized" @scroll="scroll" @timeupdate="saveProgress" :podcast="podcast" :track="playlist[currentTrack]"
                              :start-at="startAt" ref="timeline" @loaded="setProgressIfExists"
                    />

                    <Controls
                        :playing="timeline?.isPlaying"
                        :muted="timeline?.isMuted"
                        @play="play"
                        @pause="pause"
                        @mute="mute"
                        @unmute="unmute"
                        @skipSeconds="skip"
                        @backSeconds="back"
                        @playbackRate="playbackRate"
                    />
                </div>
            </div>
        </div>

        <Playlist
            :playlist="playlist"
            @play-track="loadTrack"
            @play-chapter="(time) => scrollTo(time)"
            :current-track="currentTrack"
            :is-playing="timeline?.isPlaying"
            :current-chapter="timeline?.currentChapter"
            v-if="!hidePlaylist && playlist.length > 1"
        />
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import Controls from "./Components/Controls.vue";
import Playlist from "./Components/Playlist.vue";
import { useAudioWorker } from "./useAudioWorker";
import { Head } from "@inertiajs/vue3";
import Timeline from "./Components/Timeline.vue";
import { brightnessByColor } from "./helpers.js";

const props = defineProps({
    podcast: {
        type: Object,
        required: true,
    },
    colors: Object,
    startAt: {
        type: [Number, String, null],
        required: false,
    },
    shareUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    subscribeUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    logUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    hidePlaylist: {
        type: Boolean,
    },
    preview: {
        type: Boolean,
    },
});

const timeline = ref(null);

const waveform = ref(null);
const wavesurfer = computed(() => waveform.value.wavesurfer);
const seek = computed(() => waveform.value?.currentTime || 0);
const title = ref(null);

const worker = useAudioWorker(timeline, props.logUrl, props.preview);

const currentTrack = ref(0);
const suppressSeek = ref(true);
const firstLoad = ref(true);
const hasPlayed = ref(false);

const playlist = computed(() => Object.values(props.podcast.episodes));
const currentEpisode = computed(() => playlist.value[currentTrack.value]);

const logoColor = computed(() => {
    const r = document.querySelector(':root');

    const brightness = brightnessByColor(props.colors.player_background);

    if (brightness > 100) {
        return 'dark'
    }

    return 'light'
})

const art = computed(() => {
    return currentEpisode.value?.artwork;
});

const interval = 15; // seconds
let startTime = 0;
let nextLogTime = 0;

onMounted(() => {
    getCurrentInstance().appContext._translations = props.translations;

    title.value = props.podcast.title;

    loadTrack(0);
});

function scroll() {
    nextLogTime = timeline.value.duration

    setTimeout(() => {
        worker.sendTrackingData({scroll: true})

        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;
    }, 200)
}

function scrollTo(time) {
    timeline.value.seekTo(time);

    saveProgress()
    scroll()
}

function finalized() {
    worker.sendTrackingData({finalized: true})

    // Move to next track if available
    if (currentTrack.value < playlist.value.length - 1) {
        loadTrack(currentTrack.value + 1);

        setTimeout(() => {
            play()
        }, 200)
    }
}

function play() {
    timeline.value.play();

    startTime = timeline.value?.currentTime;

    if (isNaN(startTime)) {
        startTime = 0;
    }

    worker.previousSecond.value = startTime;
    nextLogTime = startTime + interval;

    requestAnimationFrame(checkTime);

    if (!hasPlayed.value) {
        worker.sendTrackingData({play: true});
        hasPlayed.value = true;
    } else {
        worker.sendTrackingData({resume: true});
    }
}

function pause() {
    timeline.value.pause();

    worker.sendTrackingData({pause: true});
}

function mute() {
    timeline.value.mute();

    worker.sendTrackingData({mute: true});
}

function unmute() {
    timeline.value.unmute();

    worker.sendTrackingData({unmute: true});
}

function skip(seconds) {
    nextLogTime = timeline.value.duration

    timeline.value.skip(seconds);

    setTimeout(() => {
        worker.sendTrackingData({forward: true});

        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;
    }, 200)
}

function back(seconds) {
    nextLogTime = timeline.value.duration

    timeline.value.back(seconds);

    setTimeout(() => {
        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;

        worker.sendTrackingData({back: true});
    }, 200)
}

function playbackRate(rate) {
    timeline.value.playbackRate(rate);

    worker.sendTrackingData({playback: rate});
}

function loadTrack(index) {
    currentTrack.value = index;

    if (!currentEpisode.value) {
        return;
    }

    worker.episodeId.value = currentEpisode.value.hash_id;
    worker.podcastId.value = props.podcast.hash_id;
    worker.episodeDuration.value = Math.ceil(timeline.value.duration || currentEpisode.value.audio.duration)
    hasPlayed.value = false;

    title.value = currentEpisode.value.title;
}

//
function saveProgress() {
    checkTime()

    const podcastId = props.podcast.hash_id;
    const episodeId = currentEpisode.value.hash_id;

    window.localStorage.setItem(
        podcastId + "-" + episodeId,
        timeline.value?.currentTime
    );
}

//
function setProgressIfExists() {
    if (currentEpisode.value) {
        const podcastId = props.podcast.hash_id;
        const episodeId = currentEpisode.value.hash_id;

        const progress = window.localStorage.getItem(
            podcastId + "-" + episodeId
        );

        if ([undefined, 'undefined'].indexOf(progress) === -1) {
            timeline.value.seekTo(progress)
        } else if (firstLoad.value && props.startAt > 0) {
            timeline.value.seekTo(props.startAt)
            firstLoad.value = false
        } else {
            timeline.value.seekTo(0)
        }

        worker.previousSecond.value = timeline.value.currentTime
        worker.episodeDuration.value = Math.ceil(timeline.value.duration)
    }
}

function checkTime() {
    if (timeline.value?.currentTime >= nextLogTime && !isNaN(nextLogTime) && !isNaN(timeline.value?.currentTime)) {
        if (timeline.value.isPlaying) {
            worker.sendTrackingData({}, nextLogTime);

            worker.previousSecond.value = timeline.value.currentTime;
        }

        nextLogTime = timeline.value.currentTime + interval;
    }
}
</script>

<style scoped>
::-webkit-scrollbar {
    width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 8px;
}

/* Handle */
::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.5);
    border-radius: 30px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.9);
}
</style>
